import { useMemo, useState } from 'react'
import { ArrowUpDown } from 'lucide-react'

export interface MMColumn {
  key: string
  label: string
  align?: 'right' | 'left' | 'center'
  format?: (v: unknown, row: Record<string, unknown>) => string
}

interface MMTableProps {
  columns: MMColumn[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows: any[]
  defaultSort?: string
  defaultDir?: 'asc' | 'desc'
  caption?: string
  emptyMessage?: string
}

export function MMTable({
  columns,
  rows,
  defaultSort,
  defaultDir = 'desc',
  caption,
  emptyMessage = 'No data available',
}: MMTableProps) {
  const [sortKey, setSortKey] = useState(defaultSort ?? columns[0]?.key ?? '')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultDir)

  const sorted = useMemo(() => {
    if (!sortKey) return rows
    return [...rows].sort((a, b) => {
      const av = a[sortKey] ?? 0
      const bv = b[sortKey] ?? 0
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv))
      return sortDir === 'desc' ? -cmp : cmp
    })
  }, [rows, sortKey, sortDir])

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  return (
    <div className="mm-card overflow-hidden">
      {caption && (
        <div className="border-b border-hairline px-6 py-4">
          <h4 className="text-lg font-semibold text-ink-dark">{caption}</h4>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-hairline">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`h-[52px] px-5 text-[11px] font-medium uppercase tracking-[1px] text-ink-soft cursor-pointer select-none whitespace-nowrap ${
                    col.align === 'right'
                      ? 'text-right'
                      : col.align === 'center'
                        ? 'text-center'
                        : 'text-left'
                  }`}
                  onClick={() => toggleSort(col.key)}
                >
                  <span className="inline-flex items-center gap-1.5">
                    {col.label}
                    <ArrowUpDown
                      className={`h-3 w-3 ${
                        sortKey === col.key
                          ? 'text-[#5A78FF]'
                          : 'text-ink-soft/40'
                      }`}
                    />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => (
              <tr
                key={i}
                className={`border-t border-hairline transition-colors hover:bg-surface-hover ${
                  i % 2 === 1 ? 'bg-surface-stripe' : ''
                }`}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`h-[60px] px-5 text-[14px] tabular-nums text-ink ${
                      col.align === 'right'
                        ? 'text-right'
                        : col.align === 'center'
                          ? 'text-center'
                          : ''
                    } ${i === 0 ? 'font-medium text-ink-dark' : ''}`}
                  >
                    {col.format
                      ? col.format(row[col.key], row)
                      : String(row[col.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-5 py-12 text-center text-sm text-ink-soft"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
